import type { ProductImageEntry } from "./types";

/** Copy for the genuine eCare import and report states captured by scenario S4. */
export const IMPORT_REPORT_CATALOGUE: readonly ProductImageEntry[] = [
  {
    id: "import.source-picker",
    module: "Importkälla",
    area: "operations",
    opens: "Import → välj system",
    setup: ["Öppna importen för den syntetiska enheten", "Välj Alfa eCare TES"],
    scenario: "S4",
    visual: { variant: 0 },
    text: {
      sv: {
        alt: "Val av importkälla för en syntetisk period",
        shows:
          "Importen börjar med att planeraren väljer vilket system exporten kommer från.",
        proves: "Källvalet leder till rätt filformat och importflöde.",
      },
      en: {
        alt: "Import source selection for a synthetic period",
        shows:
          "The import starts with the planner selecting which system produced the export.",
        proves:
          "The source selection opens the corresponding file format and import flow.",
      },
    },
    use: ["pages/Plattform.tsx"],
  },
  {
    id: "import.files-staged",
    module: "Valda rapportfiler",
    area: "operations",
    opens: "Import → välj rapportfiler",
    setup: ["Välj Alfa eCare TES", "Lägg till de genererade rapportfilerna"],
    scenario: "S4",
    visual: { variant: 0 },
    text: {
      sv: {
        alt: "Syntetiska rapportfiler redo för analys",
        shows: "De valda eCare-filerna visas tillsammans före analysen.",
        proves: "Flera rapporter kan granskas som en periodimport.",
      },
      en: {
        alt: "Synthetic report files ready for analysis",
        shows: "The selected eCare files are shown together before analysis.",
        proves: "Several reports can be reviewed as one period import.",
      },
    },
    use: ["pages/Plattform.tsx"],
  },
  {
    id: "import.preview-summary",
    module: "Importanalys",
    area: "operations",
    opens: "Import → analysera filer",
    setup: ["Analysera de syntetiska eCare-rapporterna"],
    scenario: "S4",
    visual: { variant: 0 },
    text: {
      sv: {
        alt: "Analys av syntetiska importerade rapporter",
        shows:
          "Förhandsgranskningen sammanfattar innehållet som parsern hittade i filerna.",
        proves:
          "Planeraren kan kontrollera importens omfattning innan den sparas.",
      },
      en: {
        alt: "Analysis of synthetic imported reports",
        shows: "The preview summarizes the content parsed from the files.",
        proves: "The planner can check the import scope before saving it.",
      },
    },
    use: ["pages/Plattform.tsx"],
  },
  {
    id: "import.resource-review",
    module: "Resursgranskning",
    area: "operations",
    opens: "Import → granska resurser",
    setup: ["Analysera rapporterna", "Öppna resursgranskningen"],
    scenario: "S4",
    visual: { variant: 0 },
    text: {
      sv: {
        alt: "Granskning av resurser från en syntetisk import",
        shows: "Granskningsvyn visar resurser och perioddata som ska sparas.",
        proves: "Importerat innehåll kan granskas före bekräftelse.",
      },
      en: {
        alt: "Review of resources from a synthetic import",
        shows:
          "The review shows resources and period data proposed for saving.",
        proves: "Imported content can be reviewed before confirmation.",
      },
    },
    use: ["pages/Plattform.tsx"],
  },
  {
    id: "import.persisted-catalog",
    module: "Sparad importerat underlag",
    area: "operations",
    opens: "Resurser → syntetisk importenhet",
    setup: ["Öppna den syntetiska enhetens sparade importunderlag"],
    scenario: "S4",
    visual: { variant: 0 },
    text: {
      sv: {
        alt: "Sparat kundunderlag från en syntetisk import",
        shows:
          "Enhetsvyn visar kundunderlag som skapats från den bekräftade importen.",
        proves:
          "Importerat underlag finns kvar i den valda enheten efter att importvyn stängts.",
      },
      en: {
        alt: "Saved client data from a synthetic import",
        shows:
          "The service-area view shows client data created by the confirmed import.",
        proves:
          "Imported data remains in the selected service area after leaving the import view.",
      },
    },
    use: ["pages/Plattform.tsx"],
  },
  {
    id: "report.plan-reality-overview",
    module: "Plan mot verklighet",
    area: "solution",
    opens: "Schema → rapport → Plan mot verklighet",
    setup: ["Öppna rapporten för det sparade importerade schemat"],
    scenario: "S4",
    visual: { variant: 0 },
    text: {
      sv: {
        alt: "Plan mot verklighet för den importerade perioden",
        shows:
          "Rapporten sammanställer planerade och importerade utförda besök för perioden.",
        proves:
          "Rapporten läser sparad perioddata och visar dess jämförelseunderlag.",
      },
      en: {
        alt: "Plan versus actual for the imported period",
        shows:
          "The report brings together planned and imported performed visits for the period.",
        proves:
          "The report reads saved period data and presents its comparison evidence.",
      },
    },
    use: ["pages/Plattform.tsx"],
  },
  {
    id: "report.builder-overview",
    module: "Rapportöversikt",
    area: "solution",
    opens: "Schema → rapport → rapportbyggare",
    setup: ["Öppna rapportbyggaren för den sparade rapporten"],
    scenario: "S4",
    visual: { variant: 0 },
    text: {
      sv: {
        alt: "Rapportbyggare med innehåll från den importerade perioden",
        shows:
          "Rapportbyggaren visar tillgängliga avsnitt för den sparade rapporten.",
        proves: "Rapportens innehåll väljs från den data som faktiskt finns.",
      },
      en: {
        alt: "Report builder with content from the imported period",
        shows:
          "The report builder lists sections available for the saved report.",
        proves:
          "Report content is selected from data that is actually available.",
      },
    },
    use: ["pages/Plattform.tsx"],
  },
  {
    id: "report.section-plan",
    module: "Plan mot utfört",
    area: "solution",
    opens: "Schema → rapport → Plan mot utfört",
    setup: ["Öppna rapportavsnittet för planerade och utförda besök"],
    scenario: "S4",
    visual: { variant: 0 },
    text: {
      sv: {
        alt: "Rapportavsnitt som jämför planerade och utförda besök",
        shows:
          "Avsnittet jämför planerade besök med de utförda tider som följde med importen.",
        proves: "Jämförelsen bygger på sparade plan- och utförandedata.",
      },
      en: {
        alt: "Report section comparing planned and performed visits",
        shows:
          "The section compares planned visits with performed times included in the import.",
        proves: "The comparison uses saved planned and performed data.",
      },
    },
    use: ["pages/Plattform.tsx"],
  },
  {
    id: "report.section-continuity",
    module: "Kontinuitet",
    area: "solution",
    opens: "Schema → rapport → kontinuitet",
    setup: [
      "Öppna kontinuitetsavsnittet i den importerade periodrapporten",
      "Använd rapportens verifierade CAIRE-förslag när kontinuitetsjämförelsen kräver det",
    ],
    scenario: "S4",
    visual: { variant: 0 },
    text: {
      sv: {
        alt: "Kontinuitetsavsnitt i rapporten för den importerade perioden",
        shows:
          "Avsnittet visar kontinuitetsunderlag från rapportens sparade perioddata.",
        proves:
          "Rapporten använder de klient- och besöksrelationer som faktiskt importerats.",
      },
      en: {
        alt: "Continuity section in the imported period report",
        shows:
          "The section presents continuity evidence from the report's saved period data.",
        proves:
          "The report uses the client and visit relationships actually imported.",
      },
    },
    use: ["pages/Plattform.tsx"],
  },
  {
    id: "report.section-method",
    module: "Underlag och metod",
    area: "solution",
    opens: "Schema → rapport → underlag och metod",
    setup: ["Öppna rapportens underlag och metod"],
    scenario: "S4",
    visual: { variant: 0 },
    text: {
      sv: {
        alt: "Rapportens underlag och metod för den importerade perioden",
        shows: "Avsnittet beskriver källor och avgränsningar för rapporten.",
        proves: "Rapporten gör sitt underlag synligt för granskning.",
      },
      en: {
        alt: "Evidence and method for the imported period report",
        shows: "The section describes the report's sources and boundaries.",
        proves: "The report makes its evidence available for review.",
      },
    },
    use: ["pages/Plattform.tsx"],
  },
];
